import { runFrameInterpolation } from '../animation/frame-interpolation.js';
import { lerpAngleShortest, dampScalar, expDampFactor } from '../math/damp.js';
import { lerpVec3ToRef } from '../math/vec3-ref.js';

const DefaultInterpolationFactor = 0.1;
const TerminationEpsilon = 1e-3;
function clampToLimit(value, lower, upper) {
  if (lower !== void 0 && value < lower) {
    return lower;
  }
  if (upper !== void 0 && value > upper) {
    return upper;
  }
  return value;
}
function interpolateArcRotateCamera(camera, scene, goal, signal, options) {
  const factor = options?.interpolationFactor ?? DefaultInterpolationFactor;
  const hasAlpha = goal.alpha !== void 0 && !isNaN(goal.alpha);
  const hasBeta = goal.beta !== void 0 && !isNaN(goal.beta);
  const hasRadius = goal.radius !== void 0 && !isNaN(goal.radius);
  const hasTarget = goal.target !== void 0;
  let goalAlpha = 0;
  let goalBeta = 0;
  let goalRadius = 0;
  const goalTarget = { x: 0, y: 0, z: 0 };
  let lastAlpha = 0;
  let lastBeta = 0;
  let lastRadius = 0;
  const lastTarget = { x: 0, y: 0, z: 0 };
  let first = true;
  const step = (deltaSeconds) => {
    if (first) {
      first = false;
      camera.inertialAlphaOffset = 0;
      camera.inertialBetaOffset = 0;
      camera.inertialRadiusOffset = 0;
      camera.inertialPanningX = 0;
      camera.inertialPanningY = 0;
      goalAlpha = hasAlpha ? goal.alpha : camera.alpha;
      goalBeta = hasBeta ? goal.beta : camera.beta;
      goalRadius = hasRadius ? goal.radius : camera.radius;
      goalTarget.x = hasTarget ? goal.target.x : camera.target.x;
      goalTarget.y = hasTarget ? goal.target.y : camera.target.y;
      goalTarget.z = hasTarget ? goal.target.z : camera.target.z;
    } else if (camera.alpha !== lastAlpha || camera.beta !== lastBeta || camera.radius !== lastRadius || camera.target.x !== lastTarget.x || camera.target.y !== lastTarget.y || camera.target.z !== lastTarget.z) {
      throw new Error("ArcRotate camera interpolation was interrupted.");
    }
    const clampedAlpha = clampToLimit(goalAlpha, camera.lowerAlphaLimit, camera.upperAlphaLimit);
    const clampedBeta = clampToLimit(goalBeta, camera.lowerBetaLimit, camera.upperBetaLimit);
    const clampedRadius = clampToLimit(goalRadius, camera.lowerRadiusLimit, camera.upperRadiusLimit);
    const t = expDampFactor(deltaSeconds, factor);
    camera.alpha = lerpAngleShortest(camera.alpha, clampedAlpha, t);
    camera.beta = lerpAngleShortest(camera.beta, clampedBeta, t);
    camera.radius = dampScalar(camera.radius, clampedRadius, t);
    lerpVec3ToRef(camera.target, goalTarget, t, camera.target);
    const radiusScale = Math.abs(clampedRadius) > 1e-6 ? Math.abs(clampedRadius) : 1;
    const alphaRemaining = Math.abs(lerpAngleShortest(camera.alpha, clampedAlpha, 1) - camera.alpha);
    const betaRemaining = Math.abs(lerpAngleShortest(camera.beta, clampedBeta, 1) - camera.beta);
    const radiusRemaining = Math.abs(clampedRadius - camera.radius) / radiusScale;
    const dx = goalTarget.x - camera.target.x;
    const dy = goalTarget.y - camera.target.y;
    const dz = goalTarget.z - camera.target.z;
    const targetRemaining = Math.hypot(dx, dy, dz) / radiusScale;
    if (alphaRemaining < TerminationEpsilon && betaRemaining < TerminationEpsilon && radiusRemaining < TerminationEpsilon && targetRemaining < TerminationEpsilon) {
      camera.alpha = clampedAlpha;
      camera.beta = clampedBeta;
      camera.radius = clampedRadius;
      camera.target.x = goalTarget.x;
      camera.target.y = goalTarget.y;
      camera.target.z = goalTarget.z;
      return false;
    }
    lastAlpha = camera.alpha;
    lastBeta = camera.beta;
    lastRadius = camera.radius;
    lastTarget.x = camera.target.x;
    lastTarget.y = camera.target.y;
    lastTarget.z = camera.target.z;
    return true;
  };
  return runFrameInterpolation(scene, step, signal);
}

export { interpolateArcRotateCamera };
//# sourceMappingURL=arc-rotate-interpolate.js.map
